import type { VisualizationEvent } from '../../types/simulationPlayback';

export type FoupOwnerKind = 'oht' | 'port' | 'slot' | 'stocker' | 'equipment' | 'unknown';

export type FoupOwnershipRecord = {
  foupId: string;
  ownerKind: FoupOwnerKind;
  ownerId: string;
  portId?: string;
  slotId?: string;
  stationId?: string;
  sinceSequence: number;
  sinceT: number;
};

export type PortInventoryRecord = {
  portId: string;
  count: number;
  foupIds: string[];
};

export type FoupOwnershipLedgerState = {
  foups: Record<string, FoupOwnershipRecord>;
  ports: Record<string, PortInventoryRecord>;
  /** OHT id → carried FOUP id (at most one). */
  ohtLoadedFoupId: Record<string, string | null>;
};

export type FoupOwnershipLedgerOptions = {
  /** Seed inventories before any events apply. */
  initialPortInventory?: Record<string, number>;
  /** Seed FOUP locations before any events apply. */
  initialOwnership?: FoupOwnershipRecord[];
};

type PortInventorySeedEvent = {
  t: number;
  sequence?: number;
  portId?: string;
  type?: string;
  kind?: string;
  action?: string;
  inventoryBefore?: number;
  availableInventoryBefore?: number;
};

/**
 * Stocker / port counts always start at 0. `inventoryBefore` on a later
 * PICKUP or DROP must not pre-fill the rack. The first OHT DROP commit
 * sets 1; each later DROP increments.
 */
export function seedPortInventoryFromFirstEvent(
  events: readonly PortInventorySeedEvent[],
  existing: Record<string, number> = {}
): Record<string, number> {
  const out = { ...existing };
  for (const event of events) {
    if (!event.portId || out[event.portId] !== undefined) continue;
    out[event.portId] = 0;
  }
  return out;
}

function cloneState(state: FoupOwnershipLedgerState): FoupOwnershipLedgerState {
  const foups: Record<string, FoupOwnershipRecord> = {};
  for (const [id, record] of Object.entries(state.foups)) {
    foups[id] = { ...record };
  }
  const ports: Record<string, PortInventoryRecord> = {};
  for (const [id, record] of Object.entries(state.ports)) {
    ports[id] = { ...record, foupIds: [...record.foupIds] };
  }
  return {
    foups,
    ports,
    ohtLoadedFoupId: { ...state.ohtLoadedFoupId },
  };
}

function ensurePort(
  state: FoupOwnershipLedgerState,
  portId: string
): PortInventoryRecord {
  let port = state.ports[portId];
  if (!port) {
    port = { portId, count: 0, foupIds: [] };
    state.ports[portId] = port;
  }
  return port;
}

function removeFoupFromPort(
  state: FoupOwnershipLedgerState,
  portId: string | undefined,
  foupId: string
): void {
  if (!portId) return;
  const port = state.ports[portId];
  if (!port) return;
  port.foupIds = port.foupIds.filter((id) => id !== foupId);
}

function clearOhtCarry(state: FoupOwnershipLedgerState, ohtId: string | undefined): void {
  if (!ohtId) return;
  if (state.ohtLoadedFoupId[ohtId]) {
    state.ohtLoadedFoupId[ohtId] = null;
  }
}

/**
 * Whether this event may mutate visual FOUP ownership or inventory.
 * Hard rule: `delta === 0` never mutates.
 * Ownership moves only on `complete`; inventory counts only on inventory_commit.
 */
export function eventMutatesVisualOwnership(event: VisualizationEvent): boolean {
  if (event.delta === 0) return false;
  if (event.intent === 'status' || event.intent === 'informational') return false;
  return (
    event.phase === 'complete' ||
    event.phase === 'inventory_commit' ||
    event.kind === 'INVENTORY_COMMIT'
  );
}

function resolveOwnerFromEvent(event: VisualizationEvent): {
  ownerKind: FoupOwnerKind;
  ownerId: string;
} | null {
  if (event.kind === 'OHT_PICKUP' && event.ohtId) {
    return { ownerKind: 'oht', ownerId: event.ohtId };
  }
  if (event.kind === 'OHT_DROP') {
    if (event.slotId) return { ownerKind: 'slot', ownerId: event.slotId };
    if (event.portId) return { ownerKind: 'port', ownerId: event.portId };
    if (event.stationId) return { ownerKind: 'stocker', ownerId: event.stationId };
  }
  if (event.kind === 'INTERNAL_TRANSFER' || event.intent === 'internal') {
    const dest = event.toPortId ?? event.slotId ?? event.portId ?? event.stationId;
    if (!dest) return null;
    if (event.slotId && dest === event.slotId) {
      return { ownerKind: 'slot', ownerId: event.slotId };
    }
    if (event.toPortId || event.portId) {
      return { ownerKind: 'port', ownerId: dest };
    }
    return { ownerKind: 'equipment', ownerId: dest };
  }
  if (event.kind === 'INVENTORY_COMMIT' || event.phase === 'inventory_commit') {
    if (event.slotId) return { ownerKind: 'slot', ownerId: event.slotId };
    if (event.portId) return { ownerKind: 'port', ownerId: event.portId };
    if (event.stationId) {
      return {
        ownerKind: event.kind === 'EQUIPMENT_TRANSFER' ? 'equipment' : 'stocker',
        ownerId: event.stationId,
      };
    }
  }
  if (event.ohtId && (event.kind === 'OHT_PICKUP' || (event.delta !== undefined && event.delta < 0))) {
    return { ownerKind: 'oht', ownerId: event.ohtId };
  }
  if (event.portId) return { ownerKind: 'port', ownerId: event.portId };
  return null;
}

function applyInventoryDelta(
  state: FoupOwnershipLedgerState,
  portId: string | undefined,
  delta: number | undefined,
  foupId: string | undefined
): void {
  if (!portId || delta === undefined || delta === 0) return;
  const port = ensurePort(state, portId);
  if (delta > 0) {
    if (foupId && !port.foupIds.includes(foupId)) {
      port.foupIds.push(foupId);
    }
    port.count = Math.max(0, port.count + delta);
  } else {
    if (foupId) {
      port.foupIds = port.foupIds.filter((id) => id !== foupId);
    } else if (port.foupIds.length > 0) {
      port.foupIds.pop();
    }
    port.count = Math.max(0, port.count + delta);
  }
}

/**
 * Pure FOUP ownership + port inventory ledger.
 * Apply events in ascending sequence order only.
 */
export class FoupOwnershipLedger {
  private state: FoupOwnershipLedgerState;
  /**
   * Bumped on every ownership/inventory mutation so a per-frame reader can skip
   * `getState()` (a full deep clone) while the ledger is unchanged.
   */
  private version = 0;

  constructor(options: FoupOwnershipLedgerOptions = {}) {
    this.state = {
      foups: {},
      ports: {},
      ohtLoadedFoupId: {},
    };

    if (options.initialPortInventory) {
      for (const [portId, count] of Object.entries(options.initialPortInventory)) {
        this.state.ports[portId] = {
          portId,
          count: Math.max(0, count),
          foupIds: [],
        };
      }
    }

    if (options.initialOwnership) {
      for (const record of options.initialOwnership) {
        this.state.foups[record.foupId] = { ...record };
        if (record.ownerKind === 'oht') {
          this.state.ohtLoadedFoupId[record.ownerId] = record.foupId;
        }
        if (record.portId) {
          const port = ensurePort(this.state, record.portId);
          if (!port.foupIds.includes(record.foupId)) {
            port.foupIds.push(record.foupId);
          }
        }
      }
    }
  }

  getState(): FoupOwnershipLedgerState {
    return cloneState(this.state);
  }

  /** Monotonic counter of applied mutations; identity of the current contents. */
  getVersion(): number {
    return this.version;
  }

  static fromState(state: FoupOwnershipLedgerState): FoupOwnershipLedger {
    const ledger = new FoupOwnershipLedger();
    ledger.state = cloneState(state);
    return ledger;
  }

  /**
   * Apply one validated visualization event.
   * Returns whether ownership/inventory mutated.
   */
  apply(event: VisualizationEvent): boolean {
    if (!eventMutatesVisualOwnership(event)) {
      return false;
    }

    const foupId = event.foupId;
    // Ownership changes only at an explicit physical/internal completion.
    // Inventory commits update counts without relocating the FOUP.
    const owner = event.phase === 'complete' ? resolveOwnerFromEvent(event) : null;
    let mutated = false;

    if (foupId && owner) {
      const previous = this.state.foups[foupId];
      if (previous?.ownerKind === 'oht') {
        clearOhtCarry(this.state, previous.ownerId);
      }
      if (previous?.portId) {
        removeFoupFromPort(this.state, previous.portId, foupId);
      }

      const next: FoupOwnershipRecord = {
        foupId,
        ownerKind: owner.ownerKind,
        ownerId: owner.ownerId,
        portId:
          owner.ownerKind === 'oht' ? undefined : event.portId ?? event.toPortId,
        slotId: event.slotId,
        stationId: event.stationId,
        sinceSequence: event.sequence,
        sinceT: event.t,
      };
      this.state.foups[foupId] = next;

      if (owner.ownerKind === 'oht') {
        this.state.ohtLoadedFoupId[owner.ownerId] = foupId;
      }

      // Track location in port.foupIds; inventory counts change only via delta.
      if (next.portId && owner.ownerKind !== 'oht') {
        const port = ensurePort(this.state, next.portId);
        if (!port.foupIds.includes(foupId)) {
          port.foupIds.push(foupId);
        }
      }

      mutated = true;
    }

    // Counts change only on the explicit inventory commit record, never at the
    // beginning of a handoff/crane span or on its ownership completion cue.
    if (
      event.delta !== undefined &&
      event.delta !== 0 &&
      (event.kind === 'INVENTORY_COMMIT' || event.phase === 'inventory_commit')
    ) {
      if (event.fromPortId && event.toPortId) {
        applyInventoryDelta(
          this.state,
          event.fromPortId,
          -Math.abs(event.delta),
          foupId
        );
        applyInventoryDelta(this.state, event.toPortId, Math.abs(event.delta), foupId);
        mutated = true;
      } else if (event.kind === 'OHT_PICKUP' || (event.delta < 0 && event.portId)) {
        applyInventoryDelta(this.state, event.portId, event.delta, foupId);
        mutated = true;
      } else if (event.kind === 'OHT_DROP' || (event.delta > 0 && event.portId)) {
        applyInventoryDelta(this.state, event.portId ?? event.toPortId, event.delta, foupId);
        mutated = true;
      } else if (
        event.kind === 'INVENTORY_COMMIT' ||
        event.phase === 'inventory_commit'
      ) {
        applyInventoryDelta(this.state, event.portId, event.delta, foupId);
        mutated = true;
      }
    }

    if (mutated) this.version += 1;
    return mutated;
  }
}
